import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material'
import yappyLogo from 'Assets/Images/PaymentMethods/yappy.svg'
import { t } from 'i18next'
import {
  getYappyLinkStatus,
  createYappyButtonOrder,
} from 'api/services/paymentServices'
import { sanitizeCellPhone } from 'Utils/functions'
import { regexCellPhone } from 'Utils/constants'
import YappyStatusScreen from './components/YappyStatusScreen'
import PaymentInfoCard from './components/PaymentInfoCard'
import YappyButtonSection from './components/YappyButtonSection'
import './YappyPayment.css'

const YAPPY_SCRIPT_SRC =
  process.env.REACT_APP_YAPPY_CDN_URL ||
  'https://bt-cdn.yappy.cloud/v1/cdn/web-component-btn-yappy.js'

const LINK_STATUS = {
  VALID: 'valid',
  EXPIRED: 'expired',
  NOT_FOUND: 'not_found',
  ERROR: 'error',
}

const T = 'YAPPY_PAYMENT_T'

const YappyPayment = () => {
  const { transactionId } = useParams()
  // A plain useRef doesn't trigger a re-render/effect re-run when its .current
  // is set — if the <btn-yappy> node isn't mounted yet on the exact render
  // where yappyScriptLoaded flips to true, the attach-listener effects below
  // would never retry and the button would stay silently non-functional.
  // A callback ref backed by state fixes that: it fires (and updates state)
  // every time the node actually mounts, whenever that happens.
  const [buttonNode, setButtonNode] = useState(null)
  const yappyButtonRef = useCallback(node => setButtonNode(node), [])

  const [paymentData, setPaymentData] = useState(null)
  const paymentDataRef = useRef(null)
  paymentDataRef.current = paymentData
  const [loading, setLoading] = useState(true)
  const [linkStatus, setLinkStatus] = useState(null)
  const [statusMessage, setStatusMessage] = useState(null)
  const [paymentStatus, setPaymentStatus] = useState('pending')
  const [yappyScriptLoaded, setYappyScriptLoaded] = useState(false)
  const yappyScriptLoadedRef = useRef(false)
  const [yappyScriptError, setYappyScriptError] = useState(false)
  // null = checking, true = available, false = unavailable
  const [yappyOnline, setYappyOnline] = useState(null)
  const [buttonError, setButtonError] = useState(null)
  const [buttonKey, setButtonKey] = useState(0)
  const [buttonVisible, setButtonVisible] = useState(true)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const isPhoneValid = regexCellPhone.test(paymentData?.phone ?? '')
  // A non-empty phone means it came pre-filled from the API — if it's already
  // invalid the user needs to see that immediately, not only after they type.
  const phoneError =
    (phoneTouched || (paymentData?.phone ?? '') !== '') && !isPhoneValid
      ? t(`${T}.PHONE_INVALID`)
      : null

  // Load Yappy button script
  useEffect(() => {
    // If the custom element is already registered (e.g. SPA re-navigation), the module
    // is in the browser's module cache and won't re-execute even if we re-add the script
    // tag. Skip loading and mark as ready immediately so the existing definition is used.
    if (customElements.get('btn-yappy')) {
      yappyScriptLoadedRef.current = true
      setYappyScriptLoaded(true)
      return
    }

    const script = document.createElement('script')
    script.src = YAPPY_SCRIPT_SRC
    script.type = 'module'
    script.async = true
    script.onload = () => {
      yappyScriptLoadedRef.current = true
      setYappyScriptLoaded(true)
    }
    script.onerror = () => setYappyScriptError(true)
    document.head.appendChild(script)
  }, [])

  // Fetch and validate payment link on mount
  useEffect(() => {
    // Discard button/widget state from a previous transaction (SPA re-navigation
    // between two different payment links without a full page reload).
    setButtonError(null)
    setYappyOnline(null)
    setButtonVisible(true)
    setPaymentStatus('pending')

    if (!transactionId) {
      setLinkStatus(LINK_STATUS.NOT_FOUND)
      setStatusMessage(t(`${T}.NO_TRANSACTION_ID`))
      setLoading(false)
      return
    }

    const fetchLinkStatus = async () => {
      try {
        const response = await getYappyLinkStatus(transactionId)

        // Non-200 API status
        if (response.Status?.Code !== 200) {
          setLinkStatus(LINK_STATUS.ERROR)
          setStatusMessage(
            response.Status?.Message || t(`${T}.API_ERROR_DEFAULT`)
          )
          return
        }

        const data = response.Data

        // { Success: false, Message: '...' } — expired or invalid link
        if (!Array.isArray(data)) {
          if (data?.Success === false) {
            setLinkStatus(LINK_STATUS.EXPIRED)
            setStatusMessage(data.Message || t(`${T}.LINK_EXPIRED_DEFAULT`))
          } else {
            setLinkStatus(LINK_STATUS.NOT_FOUND)
            setStatusMessage(t(`${T}.LINK_NOT_FOUND`))
          }
          return
        }

        // Empty array — no records
        if (data.length === 0) {
          setLinkStatus(LINK_STATUS.NOT_FOUND)
          setStatusMessage(t(`${T}.LINK_NOT_FOUND_FOR_LINK`))
          return
        }

        const item = data[0]
        const amount = Number(item.AmounPay)

        // El monto es un dato financiero mostrado antes de cobrar; ante un
        // valor faltante o inválido se trata como error en vez de rellenar
        // con un default (mostrar "$0.00" engañaría al cliente sobre lo que
        // va a pagar).
        if (!Number.isFinite(amount) || amount <= 0) {
          setLinkStatus(LINK_STATUS.ERROR)
          setStatusMessage(t(`${T}.HTTP_ERROR_LOAD`))
          return
        }

        setPaymentData({
          amount,
          quoteNumber: item.DocumentNumber,
          customerName: item.CustomerNameDocument,
          phone: sanitizeCellPhone(item.Phone),
          email: item.Email || item.CustomerEmail || null,
        })
        setLinkStatus(LINK_STATUS.VALID)
      } catch (err) {
        setLinkStatus(LINK_STATUS.ERROR)

        if (err.response) {
          const apiMessage =
            err.response.data?.Status?.Message ||
            err.response.data?.Data?.Message
          setStatusMessage(apiMessage || t(`${T}.HTTP_ERROR_LOAD`))
        } else if (err.request) {
          setStatusMessage(t(`${T}.NETWORK_ERROR`))
        } else {
          setStatusMessage(t(`${T}.UNEXPECTED_ERROR`))
        }
      } finally {
        setLoading(false)
      }
    }

    fetchLinkStatus()
  }, [transactionId])

  // Covers two cases:
  // 1. isYappyOnline fired before the element listener was ready (race condition) → also listened on globalThis.
  // 2. The script never loads → the global 8s fallback resolves the spinner regardless.
  useEffect(() => {
    const onWindowYappyOnline = e => setYappyOnline(!!e.detail)
    globalThis.addEventListener('isYappyOnline', onWindowYappyOnline)

    const fallback = setTimeout(() => {
      if (yappyScriptLoadedRef.current) {
        setYappyOnline(prev => (prev === null ? true : prev))
      } else {
        setYappyScriptError(true)
      }
    }, 8000)

    return () => {
      globalThis.removeEventListener('isYappyOnline', onWindowYappyOnline)
      clearTimeout(fallback)
    }
  }, [])

  // Restore button visibility once the remounted instance is online
  useEffect(() => {
    if (yappyOnline === true) setButtonVisible(true)
  }, [yappyOnline])

  // Re-attach isYappyOnline listener whenever the button element (re)mounts
  // (after an error, or when transactionId changes and gets a fresh widget instance)
  useEffect(() => {
    if (!yappyScriptLoaded || !buttonNode) return
    const onYappyOnline = event => setYappyOnline(!!event.detail)
    buttonNode.addEventListener('isYappyOnline', onYappyOnline)
    return () => buttonNode.removeEventListener('isYappyOnline', onYappyOnline)
  }, [yappyScriptLoaded, buttonNode])

  useEffect(() => {
    if (!yappyScriptLoaded || !buttonNode) return

    const btn = buttonNode
    let isProcessingClick = false

    // The widget's own internal 'eventClick' doesn't reliably reach a listener
    // attached directly to the element in every browser (same class of issue
    // already worked around for 'isYappyOnline' above) — also listen on
    // globalThis as a fallback. The synchronous guard below prevents both
    // listeners from creating the payment order twice for the same click.
    const onButtonClick = async event => {
      // Only react to events that genuinely originate from this widget
      // instance — the globalThis registration above is a browser-quirk
      // fallback, not an invitation for arbitrary code to dispatch a fake
      // 'eventClick' and trigger a real order creation.
      if (event && event.target !== btn) return
      if (isProcessingClick) return
      isProcessingClick = true
      const phone = paymentDataRef.current?.phone ?? ''
      if (!regexCellPhone.test(phone)) {
        setPhoneTouched(true)
        setButtonError(t(`${T}.PHONE_INVALID`))
        isProcessingClick = false
        return
      }
      setButtonError(null)
      btn.isButtonLoading = true
      try {
        const response = await createYappyButtonOrder({
          id: transactionId,
          phone,
        })

        if (response.Status?.Code !== 200) {
          setButtonError(
            response.Status?.Message || t(`${T}.PROCESS_PAYMENT_ERROR`)
          )
          setButtonVisible(false)
          setYappyOnline(null)
          setButtonKey(k => k + 1)
          return
        }

        const orderData = {
          transactionId: response.Data?.transactionId,
          token: response.Data?.token,
          documentName: response.Data?.documentName,
        }
        if (typeof btn.eventPayment === 'function') {
          btn.eventPayment(orderData)
        } else {
          btn.dispatchEvent(
            new CustomEvent('eventPayment', { detail: orderData })
          )
        }
        btn.isButtonLoading = false
      } catch (err) {
        setButtonVisible(false)
        setYappyOnline(null)
        setButtonKey(k => k + 1)
        if (err.response) {
          const apiMessage =
            err.response.data?.Status?.Message ||
            err.response.data?.Data?.Message
          setButtonError(apiMessage || t(`${T}.HTTP_ERROR_PROCESS`))
        } else if (err.request) {
          setButtonError(t(`${T}.NETWORK_ERROR`))
        } else {
          setButtonError(t(`${T}.UNEXPECTED_ERROR`))
        }
      } finally {
        isProcessingClick = false
      }
    }

    // detail carries the confirmation number (e.g. 'CMAWK-40490860'); the
    // widget's default content is left visible instead of navigating to our
    // own confirmation screen.
    const onSuccess = () => {}

    // 'EXPIRED' (5-minute confirmation window elapsed) and 'DENIED'
    // (payment rejected) leave the Yappy widget's own content visible
    // instead of navigating to our error screen.
    const onError = event => {
      btn.isButtonLoading = false
      if (event.detail === 'EXPIRED' || event.detail === 'DENIED') return
      setPaymentStatus('failed')
    }

    btn.addEventListener('eventClick', onButtonClick)
    btn.addEventListener('eventSuccess', onSuccess)
    btn.addEventListener('eventError', onError)
    globalThis.addEventListener('eventClick', onButtonClick)

    return () => {
      btn.removeEventListener('eventClick', onButtonClick)
      btn.removeEventListener('eventSuccess', onSuccess)
      btn.removeEventListener('eventError', onError)
      globalThis.removeEventListener('eventClick', onButtonClick)
    }
  }, [yappyScriptLoaded, buttonNode, transactionId])

  // --- Loading ---
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5">
        <CircularProgress size={60} />
      </Box>
    )
  }

  // --- Expired / not found / error ---
  if (linkStatus !== LINK_STATUS.VALID) {
    return <YappyStatusScreen variant={linkStatus} message={statusMessage} />
  }

  // --- Payment confirmed ---
  if (paymentStatus === 'confirmed') {
    return <YappyStatusScreen variant="confirmed" />
  }

  // --- Payment failed ---
  if (paymentStatus === 'failed') {
    return (
      <YappyStatusScreen variant="failed" message={t(`${T}.FAILED_BODY`)} />
    )
  }

  // --- Payment pending — main view ---
  return (
    <Box className="yappy-payment-page">
      <Box className="yappy-payment-card">
        <Box className="yappy-card-header">
          <Typography className="yappy-card-title">
            {t(`${T}.PAGE_TITLE`)}
          </Typography>
          <Typography className="yappy-card-subtitle">
            {t(`${T}.QUOTE_NUMBER_LABEL`)} {paymentData.quoteNumber}
          </Typography>
        </Box>

        <Divider />

        <Box className="yappy-logo-area">
          <img src={yappyLogo} alt="Yappy" className="yappy-logo-img" />
        </Box>

        <Box className="yappy-card-body">
          <PaymentInfoCard
            paymentData={paymentData}
            phoneError={phoneError}
            onPhoneChange={phone => {
              setPhoneTouched(true)
              setPaymentData(prev => ({ ...prev, phone }))
            }}
          />
        </Box>

        {buttonError && (
          <Alert severity="error" sx={{ mx: 3, mb: 1, fontSize: '1.2rem' }}>
            {buttonError}
          </Alert>
        )}
        <Box className="yappy-card-footer">
          <button
            className="yappy-cancel-btn"
            onClick={() => globalThis.history.back()}>
            {t(`${T}.CANCEL_LABEL`)}
          </button>
          <YappyButtonSection
            buttonKey={buttonKey}
            transactionId={transactionId}
            buttonVisible={buttonVisible}
            isPhoneValid={isPhoneValid}
            yappyButtonRef={yappyButtonRef}
            yappyScriptLoaded={yappyScriptLoaded}
            yappyScriptError={yappyScriptError}
            yappyOnline={yappyOnline}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default YappyPayment
